import { openDayRoutePanel } from "./day-route-panel";
import { icon, type IconName } from "./icons";
import { applyStaticTranslations, i18n, type MessageKey } from "./i18n";
import { stops } from "./stops";
import {
  TRAVEL_MODES,
  TRAVEL_MODE_ICON_NAME,
  type Excursion,
  type Hotel,
  type Place,
  type Stop,
  type TravelDetails,
  type TravelMode,
} from "./types";
import { parseCoords } from "./coords";
import { reverseLookup, searchCity } from "./geocode";
import { focusPoint, setPlacePickMode } from "./map";

type DialogElements = {
  dialog: HTMLElement;
  form: HTMLFormElement;
  // General tab
  city: HTMLInputElement;
  dateFrom: HTMLInputElement;
  dateTo: HTMLInputElement;
  arriveGroup: HTMLElement;
  departGroup: HTMLElement;
  notes: HTMLTextAreaElement;
  // Hotel tab
  hotelName: HTMLInputElement;
  hotelAddress: HTMLInputElement;
  hotelMaps: HTMLInputElement;
  hotelCheckIn: HTMLInputElement;
  hotelCheckOut: HTMLInputElement;
  hotelPrice: HTMLInputElement;
  hotelCurrency: HTMLSelectElement;
  // Travel tab — arrive details
  arriveCarrier: HTMLInputElement;
  arriveTicketRef: HTMLInputElement;
  arriveDepTime: HTMLInputElement;
  arriveArrTime: HTMLInputElement;
  arrivePrice: HTMLInputElement;
  arriveCurrency: HTMLInputElement;
  // Travel tab — depart details
  departCarrier: HTMLInputElement;
  departTicketRef: HTMLInputElement;
  departDepTime: HTMLInputElement;
  departArrTime: HTMLInputElement;
  departPrice: HTMLInputElement;
  departCurrency: HTMLInputElement;
  // Places tab
  placesSearch: HTMLInputElement;
  placesAddBtn: HTMLButtonElement;
  placesPickBtn: HTMLButtonElement;
  placesExportKmlBtn: HTMLButtonElement;
  placesRouteBtn: HTMLButtonElement;
  placesRoundtripBtn: HTMLButtonElement;
  placesList: HTMLUListElement;
  // Excursions tab
  excursionsList: HTMLUListElement;
  excursionsEmpty: HTMLParagraphElement;
  excName: HTMLInputElement;
  excDateFrom: HTMLInputElement;
  excDateTo: HTMLInputElement;
  excTimeFrom: HTMLInputElement;
  excTimeTo: HTMLInputElement;
  excCoords: HTMLInputElement;
  excMaps: HTMLInputElement;
  excNotes: HTMLTextAreaElement;
  excError: HTMLParagraphElement;
  excSubmitBtn: HTMLButtonElement;
  excCancelBtn: HTMLButtonElement;
  // Buttons
  cancelBtn: HTMLButtonElement;
  closeX: HTMLButtonElement;
};

const TRAVEL_LABEL_KEY: Record<TravelMode, MessageKey> = {
  plane: "travelPlane",
  train: "travelTrain",
  bus: "travelBus",
  car: "travelCar",
};

let els: DialogElements | null = null;
let currentStopId: string | null = null;
let currentPlaces: Place[] = [];
let currentExcursions: Excursion[] = [];
/** Id of the excursion the form is editing, or null when it's an "add" form. */
let editingExcursionId: string | null = null;

export function initEditDialog(): void {
  const dialog = required<HTMLElement>("#edit-dialog");
  const form = required<HTMLFormElement>("#edit-form");
  els = {
    dialog,
    form,
    city: required<HTMLInputElement>("#edit-city"),
    dateFrom: required<HTMLInputElement>("#edit-date-from"),
    dateTo: required<HTMLInputElement>("#edit-date-to"),
    arriveGroup: required<HTMLElement>("#edit-arrive-by"),
    departGroup: required<HTMLElement>("#edit-depart-by"),
    notes: required<HTMLTextAreaElement>("#edit-notes"),
    hotelName: required<HTMLInputElement>("#edit-hotel-name"),
    hotelAddress: required<HTMLInputElement>("#edit-hotel-address"),
    hotelMaps: required<HTMLInputElement>("#edit-hotel-maps"),
    hotelCheckIn: required<HTMLInputElement>("#edit-hotel-checkin"),
    hotelCheckOut: required<HTMLInputElement>("#edit-hotel-checkout"),
    hotelPrice: required<HTMLInputElement>("#edit-hotel-price"),
    hotelCurrency: required<HTMLSelectElement>("#edit-hotel-currency"),
    arriveCarrier: required<HTMLInputElement>("#edit-arrive-carrier"),
    arriveTicketRef: required<HTMLInputElement>("#edit-arrive-ticket-ref"),
    arriveDepTime: required<HTMLInputElement>("#edit-arrive-dep-time"),
    arriveArrTime: required<HTMLInputElement>("#edit-arrive-arr-time"),
    arrivePrice: required<HTMLInputElement>("#edit-arrive-price"),
    arriveCurrency: required<HTMLInputElement>("#edit-arrive-currency"),
    departCarrier: required<HTMLInputElement>("#edit-depart-carrier"),
    departTicketRef: required<HTMLInputElement>("#edit-depart-ticket-ref"),
    departDepTime: required<HTMLInputElement>("#edit-depart-dep-time"),
    departArrTime: required<HTMLInputElement>("#edit-depart-arr-time"),
    departPrice: required<HTMLInputElement>("#edit-depart-price"),
    departCurrency: required<HTMLInputElement>("#edit-depart-currency"),
    placesSearch: required<HTMLInputElement>("#edit-places-search"),
    placesAddBtn: required<HTMLButtonElement>("#edit-places-add"),
    placesPickBtn: required<HTMLButtonElement>("#edit-places-pick"),
    placesExportKmlBtn: required<HTMLButtonElement>("#edit-places-export-kml"),
    placesRouteBtn: required<HTMLButtonElement>("#edit-places-route"),
    placesRoundtripBtn: required<HTMLButtonElement>("#edit-places-roundtrip"),
    placesList: required<HTMLUListElement>("#edit-places-list"),
    excursionsList: required<HTMLUListElement>("#edit-excursions-list"),
    excursionsEmpty: required<HTMLParagraphElement>("#edit-excursions-empty"),
    excName: required<HTMLInputElement>("#edit-excursion-name"),
    excDateFrom: required<HTMLInputElement>("#edit-excursion-date-from"),
    excDateTo: required<HTMLInputElement>("#edit-excursion-date-to"),
    excTimeFrom: required<HTMLInputElement>("#edit-excursion-time-from"),
    excTimeTo: required<HTMLInputElement>("#edit-excursion-time-to"),
    excCoords: required<HTMLInputElement>("#edit-excursion-coords"),
    excMaps: required<HTMLInputElement>("#edit-excursion-maps"),
    excNotes: required<HTMLTextAreaElement>("#edit-excursion-notes"),
    excError: required<HTMLParagraphElement>("#edit-excursion-error"),
    excSubmitBtn: required<HTMLButtonElement>("#edit-excursion-submit"),
    excCancelBtn: required<HTMLButtonElement>("#edit-excursion-cancel"),
    cancelBtn: required<HTMLButtonElement>("#edit-cancel"),
    closeX: dialog.querySelector<HTMLButtonElement>(".dialog-close")!,
  };

  buildModeGroup(els.arriveGroup);
  buildModeGroup(els.departGroup);
  initTabs(dialog);

  els.placesAddBtn.appendChild(icon("plus", { size: 16 }));
  els.placesPickBtn.appendChild(icon("map-pin", { size: 16 }));
  els.placesExportKmlBtn.appendChild(icon("download", { size: 16 }));

  els.dateFrom.addEventListener("change", () => {
    els!.dateTo.min = els!.dateFrom.value || "";
    if (els!.dateTo.value && els!.dateFrom.value && els!.dateTo.value < els!.dateFrom.value) {
      els!.dateTo.value = els!.dateFrom.value;
    }
    syncExcursionDateMins();
  });

  els.excDateFrom.addEventListener("change", () => syncExcursionDateMins());

  els.hotelCheckIn.addEventListener("change", () => {
    els!.hotelCheckOut.min = els!.hotelCheckIn.value || "";
  });

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    saveAndClose();
  });

  els.cancelBtn.addEventListener("click", () => closeDialog());
  els.closeX.addEventListener("click", () => closeDialog());

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && els?.dialog.classList.contains("open")) closeDialog();
  });

  els.placesAddBtn.addEventListener("click", () => {
    const query = els!.placesSearch.value.trim();
    if (query) addPlaceByGeocode(query);
  });

  els.placesSearch.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const query = els!.placesSearch.value.trim();
      if (query) addPlaceByGeocode(query);
    }
  });

  els.placesExportKmlBtn.addEventListener("click", () => exportPlacesAsKml());
  els.placesRouteBtn.addEventListener("click", () => planAndClose(false));
  els.placesRoundtripBtn.addEventListener("click", () => planAndClose(true));

  els.placesPickBtn.addEventListener("click", () => {
    const btn = els!.placesPickBtn;
    const isActive = btn.classList.contains("active");
    if (isActive) {
      btn.classList.remove("active");
      btn.title = i18n.t("editPlacesClickHint");
      btn.setAttribute("aria-label", i18n.t("editPlacesClickHint"));
      setPlacePickMode(null);
    } else {
      btn.classList.add("active");
      btn.title = i18n.t("editPlacesClickActive");
      btn.setAttribute("aria-label", i18n.t("editPlacesClickActive"));
      setPlacePickMode((lat, lon) => {
        btn.classList.remove("active");
        btn.title = i18n.t("editPlacesClickHint");
        btn.setAttribute("aria-label", i18n.t("editPlacesClickHint"));
        addPlaceByCoords(lat, lon);
      });
    }
  });

  els.excSubmitBtn.addEventListener("click", () => submitExcursionForm());
  els.excCancelBtn.addEventListener("click", () => resetExcursionForm());

  // The dialog is one big <form> whose submit is Save-and-close, so a bare Enter inside an
  // excursion field would close the dialog with the draft unsaved. Commit the draft instead.
  for (const field of [els.excName, els.excDateFrom, els.excDateTo, els.excTimeFrom, els.excTimeTo, els.excCoords, els.excMaps]) {
    field.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        submitExcursionForm();
      }
    });
  }

  i18n.addEventListener("change", () => {
    if (!els) return;
    rebuildModeGroup(els.arriveGroup);
    rebuildModeGroup(els.departGroup);
    renderExcursionsList();
    syncExcursionFormMode();
  });
}

export type EditTab = "general" | "hotel" | "travel" | "places" | "excursions";

export function openEditDialog(stop: Stop, opts?: { initialTab?: EditTab }): void {
  if (!els) return;
  currentStopId = stop.id;
  const all = stops.all();
  const idx = all.findIndex((s) => s.id === stop.id);
  const previous = idx > 0 ? all[idx - 1] : null;

  const dateFromDefault = stop.dateFrom ?? previous?.dateTo ?? "";
  const arriveByDefault = stop.arriveBy ?? previous?.departBy;

  // General tab
  els.city.value = stop.city || stop.name;
  els.dateFrom.value = dateFromDefault;
  els.dateTo.value = stop.dateTo ?? "";
  els.dateTo.min = dateFromDefault;
  els.notes.value = stop.notes ?? "";
  setSelectedMode(els.arriveGroup, arriveByDefault);
  setSelectedMode(els.departGroup, stop.departBy);

  // Hotel tab
  const h = stop.hotel ?? {};
  els.hotelName.value = h.name ?? "";
  els.hotelAddress.value = h.address ?? "";
  els.hotelMaps.value = h.googleMapsUrl ?? "";
  els.hotelCheckIn.value = h.checkIn ?? "";
  els.hotelCheckOut.value = h.checkOut ?? "";
  els.hotelCheckOut.min = h.checkIn ?? "";
  els.hotelPrice.value = h.price != null ? String(h.price) : "";
  els.hotelCurrency.value = h.currency ?? "EUR";

  // Travel tab — arrive
  const ad = stop.arriveDetails ?? {};
  els.arriveCarrier.value = ad.carrier ?? "";
  els.arriveTicketRef.value = ad.ticketRef ?? "";
  els.arriveDepTime.value = ad.departureTime ?? "";
  els.arriveArrTime.value = ad.arrivalTime ?? "";
  els.arrivePrice.value = ad.price != null ? String(ad.price) : "";
  els.arriveCurrency.value = ad.currency ?? "";

  // Travel tab — depart
  const dd = stop.departDetails ?? {};
  els.departCarrier.value = dd.carrier ?? "";
  els.departTicketRef.value = dd.ticketRef ?? "";
  els.departDepTime.value = dd.departureTime ?? "";
  els.departArrTime.value = dd.arrivalTime ?? "";
  els.departPrice.value = dd.price != null ? String(dd.price) : "";
  els.departCurrency.value = dd.currency ?? "";

  // Places tab
  currentPlaces = stop.places ? [...stop.places] : [];
  els.placesSearch.value = "";
  renderPlacesList();

  // Excursions tab
  currentExcursions = stop.excursions ? [...stop.excursions] : [];
  resetExcursionForm();
  renderExcursionsList();

  if (opts?.initialTab) activateTab(els.dialog, opts.initialTab);
  else resetToFirstTab(els.dialog);
  applyStaticTranslations(els.dialog);
  els.dialog.classList.add("open");
}

function closeDialog(): void {
  setPlacePickMode(null);
  if (els?.placesPickBtn.classList.contains("active")) {
    els.placesPickBtn.classList.remove("active");
    els.placesPickBtn.title = i18n.t("editPlacesClickHint");
    els.placesPickBtn.setAttribute("aria-label", i18n.t("editPlacesClickHint"));
  }
  els?.dialog.classList.remove("open");
  currentStopId = null;
}

function saveStop(): string | null {
  if (!els || !currentStopId) return null;
  const stopId = currentStopId;
  const prevHotel = stops.get(stopId)?.hotel;

  const hotel = collectHotelFields();

  if (hotel?.address) {
    if (hotel.address === prevHotel?.address && prevHotel.lat != null && prevHotel.lon != null) {
      hotel.lat = prevHotel.lat;
      hotel.lon = prevHotel.lon;
    } else if (hotel.address !== prevHotel?.address) {
      searchCity(hotel.address)
        .then((results) => {
          if (!results.length) return;
          const lat = parseFloat(results[0].lat);
          const lon = parseFloat(results[0].lon);
          if (isNaN(lat) || isNaN(lon)) return;
          const current = stops.get(stopId);
          if (!current?.hotel) return;
          stops.update(stopId, { hotel: { ...current.hotel, lat, lon } });
        })
        .catch(() => { /* geocoding failure is silent */ });
    }
  }

  const patch: Partial<Stop> = {
    dateFrom: els.dateFrom.value || undefined,
    dateTo: els.dateTo.value || undefined,
    arriveBy: getSelectedMode(els.arriveGroup),
    departBy: getSelectedMode(els.departGroup),
    notes: els.notes.value.trim() || undefined,
    hotel,
    arriveDetails: collectTravelDetails("arrive"),
    departDetails: collectTravelDetails("depart"),
    places: currentPlaces.length > 0 ? currentPlaces : undefined,
    excursions: currentExcursions.length > 0 ? currentExcursions : undefined,
  };
  stops.update(stopId, patch);
  return stopId;
}

function saveAndClose(): void {
  const stopId = saveStop();
  if (stopId) closeDialog();
}

function planAndClose(roundtrip: boolean): void {
  const stopId = saveStop();
  if (!stopId) return;
  closeDialog();
  const stop = stops.get(stopId);
  if (!stop) return;
  const hotel = stop.hotel;
  const options =
    roundtrip && hotel?.lat != null && hotel.lon != null
      ? { roundtrip: { lat: hotel.lat, lon: hotel.lon, name: hotel.name ?? stop.name } }
      : {};
  openDayRoutePanel(stop, options);
}

function collectHotelFields(): Hotel | undefined {
  if (!els) return undefined;
  const priceRaw = parseFloat(els.hotelPrice.value);
  const hotel: Hotel = {
    name: els.hotelName.value.trim() || undefined,
    address: els.hotelAddress.value.trim() || undefined,
    googleMapsUrl: els.hotelMaps.value.trim() || undefined,
    checkIn: els.hotelCheckIn.value || undefined,
    checkOut: els.hotelCheckOut.value || undefined,
    price: isNaN(priceRaw) ? undefined : priceRaw,
    currency: els.hotelCurrency.value || undefined,
  };
  const hasData = Object.values(hotel).some((v) => v !== undefined);
  return hasData ? hotel : undefined;
}

function collectTravelDetails(direction: "arrive" | "depart"): TravelDetails | undefined {
  if (!els) return undefined;
  const carrier = direction === "arrive" ? els.arriveCarrier : els.departCarrier;
  const ticketRef = direction === "arrive" ? els.arriveTicketRef : els.departTicketRef;
  const depTime = direction === "arrive" ? els.arriveDepTime : els.departDepTime;
  const arrTime = direction === "arrive" ? els.arriveArrTime : els.departArrTime;
  const priceEl = direction === "arrive" ? els.arrivePrice : els.departPrice;
  const currencyEl = direction === "arrive" ? els.arriveCurrency : els.departCurrency;
  const priceRaw = parseFloat(priceEl.value);
  const details: TravelDetails = {
    carrier: carrier.value.trim() || undefined,
    ticketRef: ticketRef.value.trim() || undefined,
    departureTime: depTime.value || undefined,
    arrivalTime: arrTime.value || undefined,
    price: isNaN(priceRaw) ? undefined : priceRaw,
    currency: currencyEl.value.trim().toUpperCase() || undefined,
  };
  const hasData = Object.values(details).some((v) => v !== undefined);
  return hasData ? details : undefined;
}

async function addPlaceByGeocode(query: string): Promise<void> {
  if (!els) return;
  try {
    const results = await searchCity(query);
    if (!results.length) return;
    const r = results[0];
    const name = r.name || r.display_name.split(",")[0].trim();
    addPlace({ id: crypto.randomUUID(), name, lat: parseFloat(r.lat), lon: parseFloat(r.lon) });
    els.placesSearch.value = "";
  } catch {
    // silently ignore geocode errors in place search
  }
}

async function addPlaceByCoords(lat: number, lon: number): Promise<void> {
  let name = `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
  try {
    const hit = await reverseLookup(lat, lon);
    if (hit) name = hit.city;
  } catch {
    // use coordinate fallback
  }
  addPlace({ id: crypto.randomUUID(), name, lat, lon });
}

function addPlace(place: Place): void {
  currentPlaces = [...currentPlaces, place];
  renderPlacesList();
}

function renderPlacesList(): void {
  if (!els) return;
  const geocodedCount = currentPlaces.filter(
    (p) => p.lat != null && p.lon != null,
  ).length;
  const stopHotel = currentStopId ? stops.get(currentStopId)?.hotel : undefined;
  const hasHotelCoords = stopHotel?.lat != null && stopHotel.lon != null;
  els.placesRouteBtn.hidden = geocodedCount < 2;
  els.placesRoundtripBtn.hidden = geocodedCount < 2 || !hasHotelCoords;
  els.placesList.replaceChildren();
  for (const place of currentPlaces) {
    const li = document.createElement("li");
    li.className = "place-item";
    li.dataset.id = place.id;

    const nameSpan = document.createElement("span");
    nameSpan.className = "place-item-name";
    nameSpan.textContent = place.name;
    nameSpan.title = place.name;

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "place-item-remove";
    removeBtn.setAttribute("aria-label", i18n.t("removePlace"));
    removeBtn.title = i18n.t("removePlace");
    removeBtn.innerHTML = `<svg class="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12"/><path d="M18 6L6 18"/></svg>`;
    removeBtn.addEventListener("click", () => {
      currentPlaces = currentPlaces.filter((p) => p.id !== place.id);
      renderPlacesList();
    });

    li.append(nameSpan);
    if (place.lat != null && place.lon != null) {
      const coords = document.createElement("span");
      coords.className = "place-item-coords";
      coords.textContent = `${place.lat.toFixed(3)}, ${place.lon.toFixed(3)}`;
      li.append(coords);
    }
    li.append(removeBtn);
    els.placesList.appendChild(li);
  }
}

// Excursions tab
//
// Unlike Places, an excursion is a small record rather than a single name, so the tab pairs a
// list with an inline form that doubles as add and edit (`editingExcursionId` picks which).
// Nothing is persisted until the dialog's Save — `currentExcursions` is the working copy, the
// same contract `currentPlaces` has.

/** Sorted by date, then start time — the order Ruta will show them in. */
function orderExcursions(a: Excursion, b: Excursion): number {
  return (
    (a.dateFrom ?? "").localeCompare(b.dateFrom ?? "") ||
    (a.timeFrom ?? "").localeCompare(b.timeFrom ?? "") ||
    a.createdAt - b.createdAt
  );
}

/**
 * An excursion can't predate the city stay it belongs to, and its end can't predate its own
 * start — the same cascading-min pattern the General tab uses for the stop's own date range.
 * Reads `els.dateFrom` live (not a snapshot taken at dialog-open time) so editing the stop's
 * start date mid-session immediately re-constrains the excursion fields.
 */
function syncExcursionDateMins(): void {
  if (!els) return;
  const cityFrom = els.dateFrom.value || "";
  els.excDateFrom.min = cityFrom;
  if (els.excDateFrom.value && cityFrom && els.excDateFrom.value < cityFrom) {
    els.excDateFrom.value = cityFrom;
  }
  const excFrom = els.excDateFrom.value || cityFrom;
  els.excDateTo.min = excFrom;
  if (els.excDateTo.value && excFrom && els.excDateTo.value < excFrom) {
    els.excDateTo.value = excFrom;
  }
}

function resetExcursionForm(): void {
  if (!els) return;
  editingExcursionId = null;
  els.excName.value = "";
  els.excDateFrom.value = "";
  els.excDateTo.value = "";
  els.excTimeFrom.value = "";
  els.excTimeTo.value = "";
  els.excCoords.value = "";
  els.excMaps.value = "";
  els.excNotes.value = "";
  showExcursionError(null);
  syncExcursionFormMode();
  syncExcursionDateMins();
}

/** Keeps the submit label and the cancel button in step with add-vs-edit. */
function syncExcursionFormMode(): void {
  if (!els) return;
  const editing = editingExcursionId != null;
  els.excSubmitBtn.textContent = i18n.t(editing ? "saveExcursion" : "addExcursion");
  if (!editing) els.excSubmitBtn.appendChild(icon("plus", { size: 16 }));
  els.excCancelBtn.hidden = !editing;
}

function showExcursionError(key: MessageKey | null): void {
  if (!els) return;
  els.excError.hidden = key == null;
  els.excError.textContent = key ? i18n.t(key) : "";
}

function loadExcursionIntoForm(exc: Excursion): void {
  if (!els) return;
  editingExcursionId = exc.id;
  els.excName.value = exc.name;
  els.excDateFrom.value = exc.dateFrom ?? "";
  els.excDateTo.value = exc.dateTo ?? "";
  els.excTimeFrom.value = exc.timeFrom ?? "";
  els.excTimeTo.value = exc.timeTo ?? "";
  els.excCoords.value =
    exc.lat != null && exc.lon != null ? `${exc.lat}, ${exc.lon}` : "";
  els.excMaps.value = exc.mapsUrl ?? "";
  els.excNotes.value = exc.notes ?? "";
  showExcursionError(null);
  syncExcursionFormMode();
  syncExcursionDateMins();
  els.excName.focus();
}

function submitExcursionForm(): void {
  if (!els) return;
  const name = els.excName.value.trim();
  if (!name) {
    showExcursionError("excursionNameRequired");
    els.excName.focus();
    return;
  }

  // Empty coords means "no coords", which is fine. Non-empty but unparseable is a typo the
  // user needs to see — silently dropping it would lose the map pin without explanation.
  const coordsRaw = els.excCoords.value.trim();
  let coords: { lat: number; lon: number } | null = null;
  if (coordsRaw) {
    coords = parseCoords(coordsRaw);
    if (!coords) {
      showExcursionError("excursionCoordsInvalid");
      els.excCoords.focus();
      return;
    }
  }

  const now = Date.now();
  const existing = editingExcursionId
    ? currentExcursions.find((e) => e.id === editingExcursionId)
    : undefined;

  const saved: Excursion = {
    id: existing?.id ?? crypto.randomUUID(),
    name,
    dateFrom: els.excDateFrom.value || undefined,
    dateTo: els.excDateTo.value || undefined,
    timeFrom: els.excTimeFrom.value || undefined,
    timeTo: els.excTimeTo.value || undefined,
    lat: coords?.lat,
    lon: coords?.lon,
    mapsUrl: els.excMaps.value.trim() || undefined,
    notes: els.excNotes.value.trim() || undefined,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };

  currentExcursions = existing
    ? currentExcursions.map((e) => (e.id === saved.id ? saved : e))
    : [...currentExcursions, saved];

  resetExcursionForm();
  renderExcursionsList();
}

function formatExcursionWhen(exc: Excursion): string {
  const time =
    exc.timeFrom && exc.timeTo
      ? `${exc.timeFrom}–${exc.timeTo}`
      : (exc.timeFrom ?? "");
  return [exc.dateFrom, time].filter(Boolean).join(" · ");
}

function renderExcursionsList(): void {
  if (!els) return;
  const ordered = [...currentExcursions].sort(orderExcursions);
  els.excursionsEmpty.hidden = ordered.length > 0;
  els.excursionsList.replaceChildren();

  for (const exc of ordered) {
    const li = document.createElement("li");
    li.className = "excursion-item";
    li.dataset.id = exc.id;
    if (exc.id === editingExcursionId) li.classList.add("editing");

    const main = document.createElement("button");
    main.type = "button";
    main.className = "excursion-item-main";
    main.addEventListener("click", () => {
      // Clicking the row pans the map when we know where it is; either way it opens for edit.
      if (exc.lat != null && exc.lon != null) focusPoint(exc.lat, exc.lon);
      loadExcursionIntoForm(exc);
    });

    const nameSpan = document.createElement("span");
    nameSpan.className = "excursion-item-name";
    nameSpan.textContent = exc.name;
    nameSpan.title = exc.name;
    main.append(nameSpan);

    const when = formatExcursionWhen(exc);
    if (when) {
      const whenSpan = document.createElement("span");
      whenSpan.className = "excursion-item-when";
      whenSpan.textContent = when;
      main.append(whenSpan);
    }

    if (exc.lat != null && exc.lon != null) {
      const pin = document.createElement("span");
      pin.className = "excursion-item-pin";
      pin.textContent = "📍";
      main.append(pin);
    }

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "place-item-remove";
    removeBtn.setAttribute("aria-label", i18n.t("removeExcursion"));
    removeBtn.title = i18n.t("removeExcursion");
    removeBtn.innerHTML = `<svg class="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12"/><path d="M18 6L6 18"/></svg>`;
    removeBtn.addEventListener("click", () => {
      currentExcursions = currentExcursions.filter((e) => e.id !== exc.id);
      // Deleting the row the form is editing would otherwise leave it pointing at nothing,
      // and the next submit would silently re-add it.
      if (editingExcursionId === exc.id) resetExcursionForm();
      renderExcursionsList();
    });

    li.append(main, removeBtn);
    els.excursionsList.appendChild(li);
  }
}

// Tab management

function initTabs(dialog: HTMLElement): void {
  const tabBar = dialog.querySelector<HTMLElement>(".tab-bar");
  if (!tabBar) return;
  tabBar.addEventListener("click", (e) => {
    const btn = (e.target as Element).closest<HTMLButtonElement>("[data-tab]");
    if (!btn) return;
    activateTab(dialog, btn.dataset.tab!);
  });
  tabBar.addEventListener("keydown", (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const btns = Array.from(tabBar.querySelectorAll<HTMLButtonElement>("[data-tab]"));
    const currentIdx = btns.findIndex((b) => b.classList.contains("active"));
    const next = e.key === "ArrowRight"
      ? btns[(currentIdx + 1) % btns.length]
      : btns[(currentIdx - 1 + btns.length) % btns.length];
    if (next) {
      activateTab(dialog, next.dataset.tab!);
      next.focus();
    }
  });
}

function activateTab(dialog: HTMLElement, tabName: string): void {
  dialog.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((btn) => {
    const active = btn.dataset.tab === tabName;
    btn.classList.toggle("active", active);
    btn.setAttribute("aria-selected", String(active));
  });
  dialog.querySelectorAll<HTMLElement>("[data-panel]").forEach((panel) => {
    panel.classList.toggle("hidden", panel.dataset.panel !== tabName);
  });
}

function resetToFirstTab(dialog: HTMLElement): void {
  const firstTab = dialog.querySelector<HTMLButtonElement>("[data-tab]");
  if (firstTab) activateTab(dialog, firstTab.dataset.tab!);
}

// Mode pill helpers

type ModeOption =
  | { value: ""; iconName: null; key: MessageKey }
  | { value: TravelMode; iconName: IconName; key: MessageKey };

function buildModeGroup(group: HTMLElement): void {
  const name = group.dataset.name ?? "mode";
  const options: ModeOption[] = [
    { value: "", iconName: null, key: "editNone" },
    ...TRAVEL_MODES.map(
      (m): ModeOption => ({
        value: m,
        iconName: TRAVEL_MODE_ICON_NAME[m],
        key: TRAVEL_LABEL_KEY[m],
      }),
    ),
  ];
  for (const opt of options) {
    const id = `${name}-${opt.value || "none"}`;
    const wrapper = document.createElement("label");
    wrapper.className = "mode-pill";
    const input = document.createElement("input");
    input.type = "radio";
    input.name = name;
    input.value = opt.value;
    input.id = id;
    if (opt.value === "") input.checked = true;
    const content = document.createElement("span");
    content.className = "mode-pill-content";
    content.dataset.key = opt.key;
    if (opt.iconName) {
      content.appendChild(icon(opt.iconName, { size: 16 }));
    } else {
      content.textContent = "—";
    }
    wrapper.title = i18n.t(opt.key);
    wrapper.setAttribute("aria-label", i18n.t(opt.key));
    wrapper.append(input, content);
    group.appendChild(wrapper);
  }
}

function rebuildModeGroup(group: HTMLElement): void {
  const current = getSelectedMode(group);
  group.replaceChildren();
  buildModeGroup(group);
  setSelectedMode(group, current);
}

function setSelectedMode(group: HTMLElement, mode: TravelMode | undefined): void {
  const value = mode ?? "";
  const inputs = group.querySelectorAll<HTMLInputElement>('input[type="radio"]');
  for (const input of inputs) {
    input.checked = input.value === value;
  }
}

function getSelectedMode(group: HTMLElement): TravelMode | undefined {
  const checked = group.querySelector<HTMLInputElement>('input[type="radio"]:checked');
  const value = checked?.value;
  if (value === "plane" || value === "train" || value === "bus" || value === "car") {
    return value;
  }
  return undefined;
}

function kmlFilename(): string {
  const stop = currentStopId ? stops.get(currentStopId) : null;
  const city = (stop?.city || stop?.name || "places").replace(/[/\\:*?"<>|]/g, "-");
  if (stop?.dateFrom) {
    const [y, m, d] = stop.dateFrom.split("-");
    return `${city}-${d}-${m}-${y}.kml`;
  }
  return `${city}.kml`;
}

function exportPlacesAsKml(): void {
  if (!currentPlaces.length) return;
  const placemarks = currentPlaces
    .map((p) => {
      const coords =
        p.lat != null && p.lon != null
          ? `<Point><coordinates>${p.lon},${p.lat},0</coordinates></Point>`
          : "";
      return `    <Placemark>\n      <name>${escapeXml(p.name)}</name>${coords}\n    </Placemark>`;
    })
    .join("\n");
  const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
${placemarks}
  </Document>
</kml>`;
  const blob = new Blob([kml], { type: "application/vnd.google-earth.kml+xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = kmlFilename();
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function required<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Element not found: ${selector}`);
  return el;
}
